import React, { useState } from 'react';
import { Form, Input, Select, Button } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import countryCodes from '../data/countryCodes.json';
import lagosLGAs from '../data/lagosLGAs.json';
import LoadingSpinner from './LoadingSpinner';

const { Option } = Select;

const MARITAL_STATUSES = [
    { value: 'single', label: 'Single' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'widowed', label: 'Widowed' },
];

const IndividualRegistrationForm = ({ onSubmit, isLoading }) => {
    const [ form ] = Form.useForm();
    const [ passwordVisible, setPasswordVisible ] = useState(false);
    const [ confirmPasswordVisible, setConfirmPasswordVisible ] = useState(false);

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onSubmit}
        >
            {/* First Name */}
            <Form.Item
                label="First Name"
                name="firstName"
                rules={[ { required: true, message: 'Please enter your first name' } ]}
                className="mb-6"
            >
                <Input placeholder="Enter your first name" size="large" />
            </Form.Item>

            {/* Last Name */}
            <Form.Item
                label="Last Name"
                name="lastName"
                rules={[ { required: true, message: 'Please enter your last name' } ]}
                className="mb-6"
            >
                <Input placeholder="Enter your last name" size="large" />
            </Form.Item>

            {/* Email Address */}
            <Form.Item
                label="Email Address"
                name="email"
                rules={[
                    { required: true, message: 'Please enter your email address' },
                    { type: 'email', message: 'Please enter a valid email address' },
                ]}
                className="mb-6"
            >
                <Input placeholder="Enter your email address" size="large" />
            </Form.Item>

            {/* Phone Number */}
            <Form.Item
                label="Phone Number"
                className="mb-6"
            >
                <div className="flex" id="phone-field">
                    <Form.Item
                        name="countryCode"
                        noStyle
                        initialValue="+234"
                    >
                        <Select
                            size="large"
                            style={{ width: '100px', textAlign: 'center' }}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.data?.country?.toLowerCase() || '').includes(input.toLowerCase()) ||
                                (option?.data?.code?.toLowerCase() || '').includes(input.toLowerCase())
                            }
                            optionLabelProp="label"
                        >
                            {countryCodes.countryCodes.map(country => (
                                <Select.Option
                                    key={country.code}
                                    value={country.code}
                                    label={country.code}
                                    data={country}
                                >
                                    <div className="flex items-center">
                                        <span>{country.code}</span>
                                        <span className="ml-2 text-gray-500 text-xs">{country.country}</span>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="phoneNumber"
                        noStyle
                        rules={[
                            { required: true, message: 'Please enter your phone number' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    const code = getFieldValue('countryCode');
                                    if (code === '+234' && value && value.startsWith('0')) {
                                        return Promise.reject(new Error('Remove the leading 0 — not needed with +234'));
                                    }
                                    return Promise.resolve();
                                },
                            }),
                        ]}
                    >
                        <Input
                            placeholder="8100000000"
                            size="large"
                            className="ml-2 flex-1"
                            onBlur={(e) => {
                                const code = form.getFieldValue('countryCode');
                                const val = e.target.value;
                                if (code === '+234' && val.startsWith('0')) {
                                    form.setFieldsValue({ phoneNumber: val.slice(1) });
                                    form.validateFields([ 'phoneNumber' ]);
                                }
                            }}
                        />
                    </Form.Item>
                </div>
            </Form.Item>

            {/* Place of Birth */}
            <Form.Item
                label="Place of Birth"
                name="placeOfBirth"
                rules={[ { required: true, message: 'Please enter your place of birth' } ]}
                className="mb-6"
            >
                <Input placeholder="e.g. Lagos, Nigeria" size="large" />
            </Form.Item>

            {/* Marital Status */}
            <Form.Item
                label="Marital Status"
                name="maritalStatus"
                rules={[ { required: true, message: 'Please select your marital status' } ]}
                className="mb-6"
            >
                <Select placeholder="Select marital status" size="large">
                    {MARITAL_STATUSES.map(s => (
                        <Option key={s.value} value={s.value}>{s.label}</Option>
                    ))}
                </Select>
            </Form.Item>

            {/* Street */}
            <Form.Item
                label="Street"
                name="street"
                rules={[ { required: true, message: 'Please enter your street address' } ]}
                className="mb-6"
            >
                <Input placeholder="Enter your street address" size="large" />
            </Form.Item>

            {/* LGA */}
            <Form.Item
                label="LGA"
                name="lga"
                rules={[ { required: true, message: 'Please select your LGA' } ]}
                className="mb-6"
            >
                <Select
                    placeholder="Select your LGA"
                    size="large"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                        (option?.children || '').toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {lagosLGAs.lagosLGAs.map(lga => (
                        <Option key={lga} value={lga}>{lga}</Option>
                    ))}
                </Select>
            </Form.Item>

            {/* Password */}
            <Form.Item
                label="Create Password"
                name="password"
                rules={[
                    { required: true, message: 'Please create a password' },
                    { min: 8, message: 'Password must be at least 8 characters' },
                    {
                        pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/,
                        message: 'Must include uppercase, lowercase, number, and symbol',
                    },
                ]}
                className="mb-6"
            >
                <Input.Password
                    placeholder="Create a password"
                    size="large"
                    visibilityToggle={{
                        visible: passwordVisible,
                        onVisibleChange: setPasswordVisible,
                    }}
                    iconRender={(visible) =>
                        visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                />
            </Form.Item>

            {/* Confirm Password */}
            <Form.Item
                label="Confirm Password"
                name="confirmPassword"
                dependencies={[ 'password' ]}
                rules={[
                    { required: true, message: 'Please confirm your password' },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error('The passwords do not match'));
                        },
                    }),
                ]}
                className="mb-8"
            >
                <Input.Password
                    placeholder="Confirm your password"
                    size="large"
                    visibilityToggle={{
                        visible: confirmPasswordVisible,
                        onVisibleChange: setConfirmPasswordVisible,
                    }}
                    iconRender={(visible) =>
                        visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                />
            </Form.Item>

            {/* Submit */}
            <Form.Item>
                <Button
                    type="primary"
                    htmlType="submit"
                    className="w-full h-[43px] submit-btn text-white text-[12px] uppercase"
                    disabled={isLoading}
                >
                    {isLoading
                        ? <LoadingSpinner size="small" color="#ffffff" className="mx-auto" />
                        : 'CREATE ACCOUNT'}
                </Button>
            </Form.Item>

            {/* Login link */}
            <div className="mt-4 text-center">
                <span className="text-gray-600">Already have an account? </span>
                <Link to="/login" className="text-green-600 font-medium">Log In</Link>
            </div>
        </Form>
    );
};

export default IndividualRegistrationForm;